import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Upload, Layers, Activity, Loader2, Mountain, FileSpreadsheet, FileText, Globe, Trees, TrendingDown } from "lucide-react";
import api, { API_BASE, formatErr } from "../lib/api";

/* ─── Sample CSV that produces a nice ridge surface ───────────────────── */
const SAMPLE_SURVEY_CSV = `Point_ID,Easting,Northing,Elevation,Description
1001,0,0,10,GL
1002,50,0,12,GL
1003,100,0,11,GL
1004,150,0,13,GL
1005,200,0,12,GL
1006,0,50,11,GL
1007,50,50,15,GL
1008,100,50,18,GL
1009,150,50,17,GL
1010,200,50,14,GL
1011,0,100,12,GL
1012,50,100,17,GL
1013,100,100,22,BM
1014,150,100,19,GL
1015,200,100,15,GL
1016,0,150,11,GL
1017,50,150,15,GL
1018,100,150,17,GL
1019,150,150,16,GL
1020,200,150,13,GL
1021,0,200,10,GL
1022,50,200,12,GL
1023,100,200,13,GL
1024,150,200,12,GL
1025,200,200,11,GL
`;

/* ─── Authed download helper (cookies) ─────────────────────────────────── */
async function downloadAuthed(url, filename) {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(href); a.remove(); }, 1000);
}

/* ─── Reusable card component (mirrors SimulationsTab) ────────────────── */
function DownloadTile({ label, sublabel, icon: Icon, onClick, testid, accent }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        display: "flex", gap: 12, padding: 12,
        border: "1px solid var(--pf-border)",
        borderRadius: 8, background: "var(--pf-surface-2)",
        textAlign: "left", cursor: "pointer", width: "100%",
        transition: "border-color 120ms",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--pf-ember)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--pf-border)")}
    >
      <div style={{
        width: 36, height: 36, borderRadius: 8,
        background: accent || "rgba(242,107,31,0.12)",
        color: accent ? "#fff" : "#F26B1F",
        display: "grid", placeItems: "center",
      }}><Icon size={18} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
        <div className="pf-muted" style={{ fontSize: 11 }}>{sublabel}</div>
      </div>
    </button>
  );
}

/* ─── Three.js TIN renderer ────────────────────────────────────────────── */
function TINViewer({ tin, contours, stations, onPick }) {
  const mountRef = useRef(null);
  // Capture latest props in refs so animate loop can read without re-mount
  const stationsRef = useRef(stations);
  const onPickRef = useRef(onPick);
  useEffect(() => { stationsRef.current = stations; }, [stations]);
  useEffect(() => { onPickRef.current = onPick; }, [onPick]);

  useEffect(() => {
    if (!tin || !mountRef.current) return;
    const mount = mountRef.current;
    while (mount.firstChild) mount.removeChild(mount.firstChild);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#0A0D14");

    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100000);
    camera.up.set(0, 0, 1);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);

    // Center & scale geometry around bbox center
    const bb = tin.bbox;
    const cx = (bb.E_min + bb.E_max) / 2;
    const cy = (bb.N_min + bb.N_max) / 2;
    const cz = (bb.Z_min + bb.Z_max) / 2;
    const span = Math.max(bb.E_max - bb.E_min, bb.N_max - bb.N_min, 1);

    // Build TIN mesh
    const verts = new Float32Array(tin.vertices.length * 3);
    tin.vertices.forEach((v, i) => {
      verts[i * 3] = v[0] - cx;
      verts[i * 3 + 1] = v[1] - cy;
      verts[i * 3 + 2] = v[2] - cz;
    });
    const idx = new Uint32Array(tin.faces.flat());
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(verts, 3));
    geom.setIndex(new THREE.BufferAttribute(idx, 1));
    geom.computeVertexNormals();
    // Vertex colours by elevation (cool→warm)
    const colours = new Float32Array(tin.vertices.length * 3);
    const zRange = Math.max(bb.Z_max - bb.Z_min, 0.001);
    tin.vertices.forEach((v, i) => {
      const t = (v[2] - bb.Z_min) / zRange;
      colours[i * 3] = 0.2 + t * 0.7;       // R
      colours[i * 3 + 1] = 0.4 + (1 - t) * 0.4; // G
      colours[i * 3 + 2] = 0.9 - t * 0.6;     // B
    });
    geom.setAttribute("color", new THREE.BufferAttribute(colours, 3));
    const mat = new THREE.MeshStandardMaterial({
      vertexColors: true, side: THREE.DoubleSide,
      flatShading: false, roughness: 0.7, metalness: 0.1,
    });
    const mesh = new THREE.Mesh(geom, mat);
    scene.add(mesh);

    // Wireframe overlay for triangle edges
    const wf = new THREE.LineSegments(
      new THREE.WireframeGeometry(geom),
      new THREE.LineBasicMaterial({ color: 0x111111, opacity: 0.25, transparent: true }),
    );
    scene.add(wf);

    // Contour overlay
    if (contours && contours.polylines_by_elevation) {
      const grp = new THREE.Group();
      Object.entries(contours.polylines_by_elevation).forEach(([elev, segments]) => {
        segments.forEach((seg) => {
          const points = seg.map(([x, y]) =>
            new THREE.Vector3(x - cx, y - cy, parseFloat(elev) - cz + 0.1));
          const g = new THREE.BufferGeometry().setFromPoints(points);
          const line = new THREE.Line(g, new THREE.LineBasicMaterial({ color: 0xF26B1F }));
          grp.add(line);
        });
      });
      scene.add(grp);
    }

    // Lights
    scene.add(new THREE.HemisphereLight(0xffffff, 0x222233, 0.6));
    const sun = new THREE.DirectionalLight(0xffffff, 0.8);
    sun.position.set(span, span, span);
    scene.add(sun);

    // ── Station markers + perpendicular cross-section visualisation
    const stationsGroup = new THREE.Group();
    scene.add(stationsGroup);
    const refreshStations = () => {
      // Clear previous
      while (stationsGroup.children.length) {
        const c = stationsGroup.children.pop();
        if (c.geometry) c.geometry.dispose();
        if (c.material) c.material.dispose();
      }
      const sts = stationsRef.current || [];
      const halfW = Math.max(span * 0.15, 5);
      sts.forEach((s, i) => {
        // Sphere marker
        const sg = new THREE.SphereGeometry(span * 0.012, 16, 16);
        const sm = new THREE.MeshStandardMaterial({
          color: i === sts.length - 1 ? 0xE14B4B : 0xF26B1F,
          emissive: 0x331100,
        });
        const sphere = new THREE.Mesh(sg, sm);
        sphere.position.set(s.E - cx, s.N - cy, (s.Z ?? bb.Z_max) - cz + span * 0.01);
        stationsGroup.add(sphere);
        // Perpendicular line indicator
        const dx = s.dirX ?? 1;
        const dy = s.dirY ?? 0;
        const norm = Math.hypot(dx, dy) || 1;
        const px = -dy / norm;
        const py = dx / norm;
        const a = new THREE.Vector3(
          s.E - cx + px * halfW, s.N - cy + py * halfW,
          (s.Z ?? bb.Z_max) - cz + span * 0.005);
        const b = new THREE.Vector3(
          s.E - cx - px * halfW, s.N - cy - py * halfW,
          (s.Z ?? bb.Z_max) - cz + span * 0.005);
        const lg = new THREE.BufferGeometry().setFromPoints([a, b]);
        stationsGroup.add(new THREE.Line(lg,
          new THREE.LineBasicMaterial({ color: 0xE14B4B, linewidth: 2 })));
      });
    };
    refreshStations();

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    camera.position.set(span * 0.8, -span * 0.8, span * 0.6);
    controls.target.set(0, 0, 0);

    // ── Click-to-place: raycast against the TIN mesh
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const onClick = (ev) => {
      if (!onPickRef.current) return;
      const rect = renderer.domElement.getBoundingClientRect();
      ndc.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, camera);
      const hits = raycaster.intersectObject(mesh);
      if (hits.length === 0) return;
      const p = hits[0].point;
      onPickRef.current({ E: p.x + cx, N: p.y + cy, Z: p.z + cz });
    };
    renderer.domElement.addEventListener("click", onClick);

    const resize = () => {
      const w = mount.clientWidth || 600;
      const h = mount.clientHeight || 400;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener("resize", resize);

    let raf = 0;
    let lastN = -1;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      // Refresh station markers on change
      const n = (stationsRef.current || []).length;
      if (n !== lastN) {
        refreshStations();
        lastN = n;
      }
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      renderer.domElement.removeEventListener("click", onClick);
      window.removeEventListener("resize", resize);
      controls.dispose();
      renderer.dispose();
      geom.dispose();
      mat.dispose();
    };
  }, [tin, contours]);

  return (
    <div
      ref={mountRef}
      data-testid="tin-viewer"
      style={{
        width: "100%", height: 380,
        borderRadius: 10, overflow: "hidden",
        border: "1px solid var(--pf-border)",
        background: "#0A0D14",
        cursor: onPick ? "crosshair" : "grab",
      }}
    />
  );
}

/* ─── SVG cross-section profile renderer ───────────────────────────────── */
function CrossSectionPanel({ data, station }) {
  if (!data || !data.samples) return null;
  const pts = data.samples.filter((s) => s.Z !== null);
  if (pts.length < 2) {
    return (
      <div data-testid="xsec-empty" className="pf-muted" style={{ fontSize: 12, padding: 12 }}>
        Section sampled outside TIN extent — try a more central station.
      </div>
    );
  }
  const W = 400, H = 220, M = 30;
  const xs = pts.map((p) => p.offset_m);
  const zs = pts.map((p) => p.Z);
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  const zMin = Math.min(...zs), zMax = Math.max(...zs);
  const sx = (x) => M + ((x - xMin) / Math.max(xMax - xMin, 1e-6)) * (W - 2 * M);
  // Y axis inverted for SVG (top = highest elevation)
  const sy = (z) => H - M - ((z - zMin) / Math.max(zMax - zMin, 1e-6)) * (H - 2 * M);
  const path = pts.map((p, i) =>
    `${i === 0 ? "M" : "L"}${sx(p.offset_m).toFixed(1)},${sy(p.Z).toFixed(1)}`).join(" ");
  // Fill polygon (down to baseline)
  const fillPath = `${path} L${sx(xMax).toFixed(1)},${H - M} L${sx(xMin).toFixed(1)},${H - M} Z`;
  return (
    <div data-testid="xsec-panel" style={{
      border: "1px solid var(--pf-border)", borderRadius: 8,
      padding: 12, background: "var(--pf-surface-2)",
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
        Cross-Section @ E={station?.E?.toFixed(1)} N={station?.N?.toFixed(1)}
      </div>
      <div className="pf-muted" style={{ fontSize: 11, marginBottom: 4 }}>
        Half-width ±{data.half_width_m}m · Step {data.step_m}m · Z range {zMin.toFixed(2)}–{zMax.toFixed(2)}m
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ background: "#FFFFFF", borderRadius: 4 }}>
        {/* axes */}
        <line x1={M} y1={H - M} x2={W - M} y2={H - M} stroke="#888" />
        <line x1={M} y1={M} x2={M} y2={H - M} stroke="#888" />
        {/* baseline label */}
        <text x={M + 4} y={H - M - 4} fontSize="10" fill="#444">{zMin.toFixed(1)}m</text>
        <text x={M + 4} y={M + 12} fontSize="10" fill="#444">{zMax.toFixed(1)}m</text>
        <text x={W - M - 22} y={H - M - 4} fontSize="10" fill="#444">+{xMax}m</text>
        <text x={M + 4} y={H - 6} fontSize="10" fill="#444">−{Math.abs(xMin)}m offset</text>
        {/* centre line */}
        <line x1={sx(0)} y1={M} x2={sx(0)} y2={H - M} stroke="#E14B4B"
          strokeDasharray="4 4" />
        {/* fill */}
        <path d={fillPath} fill="rgba(242,107,31,0.20)" stroke="none" />
        {/* profile line */}
        <path d={path} fill="none" stroke="#1F3A5F" strokeWidth="2" />
        {/* sample dots */}
        {pts.map((p, i) => (
          <circle key={i} cx={sx(p.offset_m)} cy={sy(p.Z)} r="1.6" fill="#1F3A5F" />
        ))}
      </svg>
    </div>
  );
}

/* ═══ T2-A Survey Upload + TIN/Contour Viewer ═══════════════════════════ */
function SurveyViewerCard({ projectId }) {
  const [text, setText] = useState(SAMPLE_SURVEY_CSV);
  const [tin, setTin] = useState(null);
  const [contours, setContours] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [interval, setIntervalVal] = useState(2);
  const [stats, setStats] = useState(null);
  // ── Click-to-place stations + cross-section
  const [pickMode, setPickMode] = useState(false);
  const [stations, setStations] = useState([]);  // [{E, N, Z, dirX, dirY}]
  const [xsec, setXsec] = useState(null);        // {samples, half_width_m, step_m}
  const [xsecBusy, setXsecBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const buf = await file.text();
    setText(buf);
  };

  const upload = async () => {
    setBusy(true);
    setErr(null);
    setStations([]);  // reset on new survey
    setXsec(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/tier2/survey/upload`, {
        format: "csv", text,
      });
      setTin(data.tin);
      setStats({ id: data.id, n_points: data.points?.length || 0 });
      const { data: cd } = await api.post(`/projects/${projectId}/tier2/survey/contours`, {
        interval: parseFloat(interval) || 1,
      });
      setContours(cd);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    } finally {
      setBusy(false);
    }
  };

  // Pick handler — invoked by TINViewer raycaster
  const onPick = React.useCallback(async (point) => {
    // Direction = from previous station to this one (or East default)
    setStations((prev) => {
      const last = prev[prev.length - 1];
      let dirX = 1, dirY = 0;
      if (last) {
        const dx = point.E - last.E;
        const dy = point.N - last.N;
        const norm = Math.hypot(dx, dy);
        if (norm > 1e-6) {
          dirX = dx / norm;
          dirY = dy / norm;
        }
      }
      const next = [...prev, { ...point, dirX, dirY }];
      // Fire cross-section request asynchronously
      (async () => {
        setXsecBusy(true);
        try {
          const { data } = await api.post(
            `/projects/${projectId}/tier2/survey/cross-section`,
            {
              station_xy: [point.E, point.N],
              direction_xy: [dirX, dirY],
              half_width_m: 30,
              sample_step_m: 1,
            });
          setXsec({ ...data, station: point });
        } catch (e) {
          setErr(formatErr(e?.response?.data?.detail) || String(e));
        } finally {
          setXsecBusy(false);
        }
      })();
      return next;
    });
  }, [projectId]);

  const clearStations = () => {
    setStations([]);
    setXsec(null);
  };

  return (
    <div data-testid="survey-viewer-card" style={{
      border: "1px solid var(--pf-border)", borderRadius: 12,
      padding: 18, background: "var(--pf-surface)",
      display: "flex", flexDirection: "column", gap: 14,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Mountain size={24} style={{ color: "#F26B1F" }} />
        <div style={{ flex: 1 }}>
          <div className="pf-h2" style={{ fontSize: 18 }}>Survey & Terrain Viewer</div>
          <div className="pf-muted" style={{ fontSize: 13 }}>
            Drag/drop a topo CSV (or paste text) → instantly see Delaunay TIN +
            contour overlay rendered with Three.js. Then click on the mesh to drop
            alignment stations and instantly extract cross-section profiles.
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 14 }}>
        <textarea
          data-testid="survey-csv-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Point_ID,Easting,Northing,Elevation,Description"
          style={{
            width: "100%", height: 140, fontFamily: "Consolas, monospace",
            fontSize: 11, padding: 10, borderRadius: 6,
            border: "1px solid var(--pf-border)",
            background: "var(--pf-surface-2)", color: "var(--pf-fg)",
            resize: "vertical",
          }}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label data-testid="survey-file-label" style={{
            border: "1px dashed var(--pf-border)", borderRadius: 6,
            padding: 10, fontSize: 12, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 6, justifyContent: "center",
          }}>
            <Upload size={14} />
            Drop CSV file here
            <input
              type="file" accept=".csv,.txt"
              onChange={handleFile}
              data-testid="survey-file-input"
              style={{ display: "none" }}
            />
          </label>
          <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}>
            Contour interval (m):
            <input
              type="number" step="0.5" min="0.1" value={interval}
              onChange={(e) => setIntervalVal(e.target.value)}
              data-testid="survey-interval-input"
              style={{
                width: 72, padding: 4, fontSize: 12, borderRadius: 4,
                border: "1px solid var(--pf-border)",
                background: "var(--pf-surface-2)", color: "var(--pf-fg)",
              }}
            />
          </label>
          <button
            data-testid="survey-upload-btn"
            onClick={upload}
            disabled={busy}
            className="pf-btn pf-btn-primary"
            style={{
              padding: "10px", fontSize: 13, fontWeight: 600,
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Activity size={14} />}
            Build TIN + Contours
          </button>
          {stats && <div data-testid="survey-stats" className="pf-muted" style={{ fontSize: 11, lineHeight: 1.7 }}>
            <div>Survey <code>{stats.id}</code></div>
            <div>{stats.n_points} pts · {tin?.n_faces} triangles</div>
            <div>Z {tin?.bbox.Z_min}m → {tin?.bbox.Z_max}m</div>
            {contours && <div>{contours.n_polylines} contour segments @ {contours.interval_m}m</div>}
          </div>}
        </div>
      </div>

      {err && <div data-testid="survey-err" style={{
        padding: 10, borderRadius: 6,
        background: "rgba(198,40,40,0.1)", color: "#FF8888",
        border: "1px solid rgba(198,40,40,0.3)", fontSize: 12,
      }}>{err}</div>}

      {tin && (
        <>
          {/* Pick-mode controls */}
          <div style={{ display: "flex", alignItems: "center", gap: 10,
                          flexWrap: "wrap" }}>
            <button
              data-testid="pick-toggle"
              onClick={() => setPickMode((p) => !p)}
              className={pickMode ? "pf-btn pf-btn-primary" : "pf-btn"}
              style={{
                padding: "6px 12px", fontSize: 12, fontWeight: 600,
                display: "inline-flex", alignItems: "center", gap: 6,
                border: pickMode ? "1px solid var(--pf-ember)" : "1px solid var(--pf-border)",
                background: pickMode ? "var(--pf-ember)" : "var(--pf-surface-2)",
                color: pickMode ? "#fff" : "var(--pf-fg)",
                borderRadius: 6, cursor: "pointer",
              }}
            >
              {pickMode ? "● Pick mode ON — click mesh to drop station"
                          : "○ Enable click-to-place"}
            </button>
            {stations.length > 0 && <>
              <span data-testid="pick-count" className="pf-muted" style={{ fontSize: 12 }}>
                {stations.length} station{stations.length === 1 ? "" : "s"}
              </span>
              <button
                data-testid="pick-clear"
                onClick={clearStations}
                style={{
                  padding: "4px 10px", fontSize: 11,
                  border: "1px solid var(--pf-border)",
                  background: "transparent", color: "var(--pf-fg)",
                  borderRadius: 4, cursor: "pointer",
                }}
              >Clear</button>
            </>}
            {xsecBusy && <span className="pf-muted" style={{ fontSize: 12,
                display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Loader2 size={12} className="animate-spin" />
              extracting cross-section…
            </span>}
          </div>

          <div style={{ display: "grid",
                          gridTemplateColumns: xsec ? "1fr 420px" : "1fr",
                          gap: 14 }}>
            <TINViewer
              tin={tin} contours={contours}
              stations={stations}
              onPick={pickMode ? onPick : null} />
            {xsec && <CrossSectionPanel data={xsec} station={xsec.station} />}
          </div>
        </>
      )}
    </div>
  );
}

/* ═══ T2-B/C/D/E single-click runs + downloads ═══════════════════════════ */
export function Tier2Panel({ projectId }) {
  const [busy, setBusy] = useState({});
  const [err, setErr] = useState(null);
  const [status, setStatus] = useState({ specs: null, eia: null, ve: null });

  const setB = (k, v) => setBusy((b) => ({ ...b, [k]: v }));

  const runSpecs = async () => {
    setB("specs", true);
    setErr(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/tier2/specs/auto-generate`, {});
      setStatus((s) => ({ ...s, specs: { id: data.id, n: data.doc.n_sections, unmapped: data.doc.unmapped_BOQ_codes?.length || 0 } }));
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    finally { setB("specs", false); }
  };

  const runEIA = async () => {
    setB("eia", true);
    setErr(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/tier2/eia`, {
        project_scale: "large", location_sensitivity: "forest",
      });
      setStatus((s) => ({ ...s, eia: { id: data.id, net: data.matrix.net_score } }));
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    finally { setB("eia", false); }
  };

  const runVE = async () => {
    setB("ve", true);
    setErr(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/tier2/value-engineering`, {
        n_years: 30, discount_rate: 0.08,
        alternatives: [
          { name: "Steel Bridge", initial_cost: 280_000_000, annual_cost: 1_500_000 },
          { name: "PSC Bridge", initial_cost: 220_000_000, annual_cost: 500_000 },
          { name: "Composite Bridge", initial_cost: 250_000_000, annual_cost: 800_000 },
        ],
      });
      setStatus((s) => ({ ...s, ve: { id: data.id, rec: data.result.recommendation, npv: data.result.lowest_NPV } }));
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    finally { setB("ve", false); }
  };

  const dlGIS = async (fmt) => {
    try {
      const sample = {
        format: fmt,
        geometry: { features: [
          { name: "Project Site", geometry: { type: "Point", coordinates: [72.877, 19.076] } },
          { name: "Centerline", geometry: { type: "LineString", coordinates: [[72.870, 19.070], [72.880, 19.080], [72.885, 19.082]] } },
        ]},
        points: fmt === "gpx" ? [{ lat: 19.076, lon: 72.877, ele: 14.5, name: "BM-1" }] : undefined,
      };
      const res = await fetch(`${API_BASE}/projects/${projectId}/tier2/gis/export`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sample),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      const ext = fmt === "shp" ? "zip" : fmt;
      a.download = `project.${ext}`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(href); a.remove(); }, 1000);
    } catch (e) { setErr(String(e)); }
  };

  const dl = (path, name) => downloadAuthed(`${API_BASE}${path}`, name).catch((e) => setErr(String(e)));

  return (
    <div data-testid="tier2-panel" style={{
      border: "1px solid var(--pf-border)", borderRadius: 12, padding: 18,
      background: "var(--pf-surface)", display: "flex", flexDirection: "column", gap: 14,
    }}>
      <div>
        <div className="pf-h2" style={{ fontSize: 18 }}>Tier-2 Project Differentiators</div>
        <div className="pf-muted" style={{ fontSize: 13 }}>
          GIS exports · Specifications · EIA · Value Engineering — single-click
          runs use representative inputs; replace via API for project-specific data.
        </div>
      </div>

      {err && <div data-testid="t2-err" style={{
        padding: 10, borderRadius: 6, background: "rgba(198,40,40,0.1)",
        color: "#FF8888", border: "1px solid rgba(198,40,40,0.3)", fontSize: 12,
      }}>{err}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        <button data-testid="t2-run-specs" onClick={runSpecs} disabled={busy.specs}
          className="pf-btn pf-btn-primary"
          style={{ padding: 10, fontSize: 13, fontWeight: 600, display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "center" }}>
          {busy.specs ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
          Auto-generate Specs
        </button>
        <button data-testid="t2-run-eia" onClick={runEIA} disabled={busy.eia}
          className="pf-btn pf-btn-primary"
          style={{ padding: 10, fontSize: 13, fontWeight: 600, display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "center" }}>
          {busy.eia ? <Loader2 size={14} className="animate-spin" /> : <Trees size={14} />}
          Run EIA Screening
        </button>
        <button data-testid="t2-run-ve" onClick={runVE} disabled={busy.ve}
          className="pf-btn pf-btn-primary"
          style={{ padding: 10, fontSize: 13, fontWeight: 600, display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "center" }}>
          {busy.ve ? <Loader2 size={14} className="animate-spin" /> : <TrendingDown size={14} />}
          Compare Alternatives
        </button>
      </div>

      <div data-testid="t2-status" className="pf-muted" style={{ fontSize: 12, lineHeight: 1.7 }}>
        {status.specs && <div>Specs <code>{status.specs.id}</code> · {status.specs.n} sections{status.specs.unmapped > 0 ? ` · ${status.specs.unmapped} unmapped` : ""}</div>}
        {status.eia && <div>EIA <code>{status.eia.id}</code> · Leopold net {status.eia.net}</div>}
        {status.ve && <div>VE <code>{status.ve.id}</code> · Recommendation: <b>{status.ve.rec}</b> · NPV ₹{(status.ve.npv / 1e7).toFixed(2)} cr</div>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
        <DownloadTile label="GIS · KML" sublabel="Google Earth"
          icon={Globe} testid="t2-dl-kml"
          accent="#34A853"
          onClick={() => dlGIS("kml")} />
        <DownloadTile label="GIS · GeoJSON" sublabel="Web maps / Mapbox"
          icon={Globe} testid="t2-dl-geojson"
          accent="#EA4335"
          onClick={() => dlGIS("geojson")} />
        <DownloadTile label="GIS · GPX" sublabel="GPS device upload"
          icon={Globe} testid="t2-dl-gpx"
          accent="#4285F4"
          onClick={() => dlGIS("gpx")} />
        <DownloadTile label="GIS · Shapefile" sublabel="ESRI ZIP (.shp+.shx+.dbf+.prj)"
          icon={Globe} testid="t2-dl-shp"
          accent="#FBBC04"
          onClick={() => dlGIS("shp")} />

        <DownloadTile label="Specs · DOCX" sublabel="Tech specs from BOQ"
          icon={FileText} testid="t2-dl-specs"
          onClick={() => dl(`/projects/${projectId}/tier2/specs.docx`, "TechSpecs.docx")} />
        <DownloadTile label="EIA · DOCX" sublabel="Screening + Leopold + EMP"
          icon={FileText} testid="t2-dl-eia"
          onClick={() => dl(`/projects/${projectId}/tier2/eia.docx`, "EIA.docx")} />
        <DownloadTile label="Layers · TIN.json" sublabel="Vertices + faces"
          icon={Layers} testid="t2-dl-tin-json"
          onClick={() => dl(`/projects/${projectId}/issue-for-construction.zip`, "Bundle.zip")} />
        <DownloadTile label="VE · LCCA.json" sublabel="Inside the IFC bundle"
          icon={FileSpreadsheet} testid="t2-dl-ve-bundle"
          onClick={() => dl(`/projects/${projectId}/issue-for-construction.zip`, "Bundle.zip")} />
      </div>
    </div>
  );
}

export default function SurveyAndTier2Tab({ projectId }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <SurveyViewerCard projectId={projectId} />
      <Tier2Panel projectId={projectId} />
    </div>
  );
}
